/**
 * Pedir entrar — la segunda puerta (ADR-021).
 *
 * TRES CAMPOS Y NADA MÁS. Sin teléfono, sin edad, sin ciudad: cada campo
 * extra cuesta conversión, y la ciudad se deduce del lugar que escriba.
 *
 * El tercero hace todo el trabajo. Es a la vez el filtro del admin, el
 * argumento de conversión, y **el primer lugar del mapa de esa persona** —
 * al aprobar se convierte en un `Place` suyo, publicado y atribuido. Entra
 * con contenido propio, no con una cuenta vacía.
 */

import { useState } from 'react';

import { Boton, Campo, Pantalla, Stack, Texto, Titulo } from '../../shared/ui/primitives.js';
import * as api from '../../shared/lib/api.js';
import { PactScreen } from '../pact/PactScreen.js';

export interface RequestAccessProps {
  onVolver?: () => void;
}

type Fase = 'formulario' | 'pacto' | 'enviada';

export function RequestAccess({ onVolver }: RequestAccessProps) {
  const [fase, setFase] = useState<Fase>('formulario');
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [lugar, setLugar] = useState('');
  const [porque, setPorque] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const completo =
    email.includes('@') && nombre.trim().length >= 2 && lugar.trim().length >= 2 && porque.trim().length >= 10;

  async function enviar() {
    if (enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await api.createAccessRequest({
        email: email.trim(),
        displayName: nombre.trim(),
        placePitch: lugar.trim(),
        placePitchWhy: porque.trim(),
      });
      setFase('enviada');
    } catch (e) {
      setError(
        e instanceof api.ApiError
          ? (e.body.message as string)
          : 'No pudimos enviar tu solicitud. Vuelve a intentar.',
      );
      setFase('formulario');
    } finally {
      setEnviando(false);
    }
  }

  /*
   * El Pacto se acepta AL MANDAR la solicitud, no al ser aprobado (ADR-028).
   * Dos razones: es parte del filtro —quien no acepta no ocupa tiempo del
   * admin— y meter una pantalla al aprobar convertiría la buena noticia en
   * un trámite.
   */
  if (fase === 'pacto') {
    return (
      <PactScreen
        puerta="solicitud"
        onAceptar={() => void enviar()}
        onRechazar={() => setFase('formulario')}
      />
    );
  }

  if (fase === 'enviada') {
    return (
      <Pantalla centrada>
        <Stack>
          <Titulo centrado>Listo. Tu solicitud está con nosotros.</Titulo>
          <Texto variante="secundario" centrado>
            Te escribimos en cuanto la revisemos. Mientras tanto, el mapa es todo tuyo.
          </Texto>
          {onVolver && <Boton onClick={onVolver}>Ver el mapa</Boton>}
        </Stack>
      </Pantalla>
    );
  }

  return (
    <Pantalla>
      <Stack>
        <header>
          <Titulo>Pedir entrar</Titulo>
          <Texto variante="secundario">
            Gourmet Urbano es por invitación. Si no tienes pase, cuéntanos de ti.
          </Texto>
        </header>

        <Campo
          id="email"
          etiqueta="Tu correo"
          tipo="email"
          inputMode="email"
          autoComplete="email"
          valor={email}
          onChange={setEmail}
        />
        <Campo
          id="nombre"
          etiqueta="¿Cómo te llamas?"
          valor={nombre}
          onChange={setNombre}
          maxLength={60}
        />

        <Stack gap="var(--gu-space-2)">
          <Titulo nivel={2}>Dinos un lugar.</Titulo>
          <Texto variante="secundario">
            Uno que recomendarías sin pensarlo. Con eso nos basta para saber si esto es para ti.
          </Texto>
        </Stack>

        <Campo
          id="lugar"
          etiqueta="¿Cuál?"
          valor={lugar}
          onChange={setLugar}
          maxLength={120}
          ayuda="El Turix, El Vilsito, la fonda de tu esquina…"
        />
        <Campo
          id="porque"
          etiqueta="¿Por qué?"
          valor={porque}
          onChange={setPorque}
          maxLength={600}
          ayuda="Dos líneas bastan. Como se lo contarías a alguien."
        />

        {error && <Texto variante="secundario">{error}</Texto>}

        <Boton onClick={() => setFase('pacto')} disabled={!completo || enviando}>
          Continuar
        </Boton>

        {onVolver && (
          <Boton variante="secundario" onClick={onVolver}>
            Tengo un pase
          </Boton>
        )}
      </Stack>
    </Pantalla>
  );
}
